/**
 * The scrap of state a front-door SSO sign-in has to carry across the trip to the identity
 * provider and back.
 *
 * At app.nexys.com the browser does not know which customer it is signing in to - that is the
 * whole point of the front door - so when it returns from the provider it cannot address the
 * right tenant API on its own. The gateway can, given the encrypted token it issued at discovery,
 * so that token (and the address it is bound to) is what gets parked here.
 *
 * sessionStorage, not localStorage: this is one sign-in in one tab and should not outlive it.
 * Every access is guarded because a locked-down or private-mode browser throws on the property
 * itself, and a sign-in must not die for want of a scratchpad.
 */

const KEY = 'nexys.sso.pending'

export interface PendingSso {
  email: string
  ssoToken: string
  ref: number
}

export function setPendingSso(pending: PendingSso): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(pending))
  } catch {
    // Non-fatal: the exchange falls back to talking to the tenant API directly, which is correct
    // on a customer's own hostname and simply fails cleanly at the shared front door.
  }
}

export function getPendingSso(): PendingSso | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.ssoToken || !parsed?.email) return null
    return { email: parsed.email, ssoToken: parsed.ssoToken, ref: Number(parsed.ref) || 0 }
  } catch {
    return null
  }
}

export function clearPendingSso(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    /* nothing to clean up if it was never written */
  }
}

/** Only a same-site relative path, so nothing on a return URL can bounce the user off-site. */
export function safeLandingPath(path: string, fallback = '/pipeline'): string {
  const value = String(path || '')
  return value.startsWith('/') && !value.startsWith('//') ? value : fallback
}
